import React, { useState, useEffect, useCallback } from "react";
import {
  Grid,
  Card,
  Button,
  Stack,
  Paper,
  InputBase,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Pagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Typography,
  Chip,
} from "@mui/material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDBadge from "components/MDBadge";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import SearchIcon from "@mui/icons-material/Search";
import DescriptionIcon from "@mui/icons-material/Description";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import VisibilityIcon from "@mui/icons-material/Visibility";
import Select from "react-select";
import Papa from "papaparse";
import { ToastContainer, toast } from "react-toastify";
import { debounce } from "lodash";
import nodata from "assets/images/nodata.png";
import { hasAdmissionPermission } from "hooks/permissions";

const BASE_URL = process.env.REACT_APP_BASE_URL;
const token = sessionStorage.getItem("token");

// Program Level Constants
const programLevels = [
  "Undergraduate (BS Programs)",
  "Graduate (MS, M-Phil Programs)",
  "Doctorate (PhD Programs)",
  "5th Semester",
];

function PreRegisterApplications() {
  // Filter States
  const [campaignOptions, setCampaignOptions] = useState([]);
  const [campusOptions, setCampusOptions] = useState([]);
  const [departmentOptions, setDepartmentOptions] = useState([]);
  const [programOptions, setProgramOptions] = useState([]);
  const [quotaOptions, setQuotaOptions] = useState([]);

  const [selectedCampaigns, setSelectedCampaigns] = useState([]);
  const [selectedCampuses, setSelectedCampuses] = useState([]);
  const [selectedDepartments, setSelectedDepartments] = useState([]);
  const [selectedPrograms, setSelectedPrograms] = useState([]);
  const [selectedQuotas, setSelectedQuotas] = useState([]);
  const [selectedProgramLevels, setSelectedProgramLevels] = useState([]);
  const [selectedGender, setSelectedGender] = useState(null);
  const [selectedNationality, setSelectedNationality] = useState(null);
  const [isPaid, setIsPaid] = useState(null);
  const [isAdded, setIsAdded] = useState(null);
  const [isCancel, setIsCancel] = useState(null);

  // Search & Pagination States
  const [searchKeyword, setSearchKeyword] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [limit] = useState(20);

  // WhatsApp Dialog
  const [whatsappDialogOpen, setWhatsappDialogOpen] = useState(false);
  const [whatsappMessages, setWhatsappMessages] = useState([""]);
  const [sendingWhatsapp, setSendingWhatsapp] = useState(false);

  const userDepartment = JSON.parse(sessionStorage.getItem("department_id"));

  // Program Level Options for react-select
  const programLevelOptions = programLevels.map((level) => ({
    value: level,
    label: level,
  }));

  // Gender Options for react-select
  const genderOptions = [
    { value: "Male", label: "Male" },
    { value: "Female", label: "Female" },
    { value: "Other", label: "Other" },
  ];

  const nationalityOptions = [
    { value: "Pakistani", label: "Pakistani" },
    { value: "Overseas", label: "Overseas" },
  ];

  // Debounced search handler
  const debouncedSearchHandler = useCallback(
    debounce((value) => {
      setDebouncedSearch(value);
      setPage(1); // Reset to first page on new search
    }, 500),
    []
  );

  useEffect(() => {
    debouncedSearchHandler(searchKeyword);
    return () => debouncedSearchHandler.cancel();
  }, [searchKeyword, debouncedSearchHandler]);

  // Initialize filters
  useEffect(() => {
    const fetchInit = async () => {
      const headers = {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      };

      const [campRes, campusRes, quotaRes] = await Promise.all([
        fetch(`${BASE_URL}/campaign/get_campaigns`, { headers }),
        fetch(`${BASE_URL}/campus/get_campuses`, { headers }),
        fetch(`${BASE_URL}/quota/get_quotas`, { headers }),
      ]);

      const campData = await campRes.json();
      const campusData = await campusRes.json();
      const quotaData = await quotaRes.json();

      const campaignOpts = (campData.campaigns || []).map((c) => ({
        value: c.id,
        label: c.name,
        status: c.status,
      }));
      const campusOpts = (campusData.campuses || []).map((camp) => ({
        value: camp.id,
        label: camp.name,
      }));
      const quotaOpts = (quotaData.quotas || []).map((q) => ({
        value: q.id,
        label: q.name,
      }));

      setCampaignOptions(campaignOpts);
      setCampusOptions(campusOpts);
      setQuotaOptions(quotaOpts);

      const active = campaignOpts.find((camp) => camp.status == 1);
      if (active) {
        setSelectedCampaigns([active]);
      }

      if (userDepartment && campusOpts.length > 0) {
        const firstCampus = campusOpts[0];
        setSelectedCampuses([firstCampus]);

        const deptRes = await fetch(
          `${BASE_URL}/department/get_departments_by_campuses`,
          {
            method: "POST",
            headers,
            body: JSON.stringify({ campus_ids: [firstCampus.value] }),
          }
        );
        const deptData = await deptRes.json();
        const deptOpts = (deptData.departments || []).map((d) => ({
          value: d.id,
          label: d.name,
        }));
        setDepartmentOptions(deptOpts);

        if (deptOpts.length > 0) {
          setSelectedDepartments([deptOpts[0]]);
        }
      }
    };

    fetchInit();
  }, []);

  // Fetch departments when campuses change
  useEffect(() => {
    if (selectedCampuses.length > 0) {
      getDepartmentsByCampuses(selectedCampuses.map((item) => item.value));
    } else {
      setDepartmentOptions([]);
      setSelectedDepartments([]);
    }
  }, [selectedCampuses]);

  // Fetch programs when departments change
  useEffect(() => {
    if (selectedDepartments.length > 0) {
      getProgramsByDepartments(selectedDepartments.map((item) => item.value));
    } else {
      setProgramOptions([]);
      setSelectedPrograms([]);
    }
  }, [selectedDepartments]);

  // Fetch applications when filters or search change
  useEffect(() => {
    if (selectedCampaigns.length > 0) {
      getFilteredApplications();
    }
  }, [
    selectedCampaigns,
    selectedCampuses,
    selectedDepartments,
    selectedPrograms,
    selectedQuotas,
    selectedProgramLevels,
    selectedGender,
    selectedNationality,
    isPaid,
    isAdded,
    isCancel,
    debouncedSearch,
    page,
  ]);

  const getDepartmentsByCampuses = async (campusIds) => {
    const requestOptions = {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ campus_ids: campusIds }),
    };

    try {
      const response = await fetch(
        `${BASE_URL}/department/get_departments_by_campuses`,
        requestOptions
      );
      const data = await response.json();
      if (data.departments) {
        const options = data.departments.map((dept) => ({
          value: dept.id,
          label: dept.name,
        }));
        setDepartmentOptions(options);
      }
    } catch (error) {
      console.error("Error fetching departments:", error);
    }
  };

  const getProgramsByDepartments = async (departmentIds) => {
    const promises = departmentIds.map((depId) => {
      return fetch(
        `${BASE_URL}/degree/get_programs_by_departments_and_campaigns`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            department_ids: [depId],
            campaign_ids: selectedCampaigns.map((item) => item.value),
          }),
        }
      );
    });

    try {
      const responses = await Promise.all(promises);
      const dataArray = await Promise.all(
        responses.map((response) => response.json())
      );

      const allPrograms = [];
      dataArray.forEach((data) => {
        if (data.degrees) {
          data.degrees.forEach((prog) => {
            if (!allPrograms.some((p) => p.value === prog.id)) {
              allPrograms.push({
                value: prog.id,
                label: prog.name,
              });
            }
          });
        }
      });

      setProgramOptions(allPrograms);
    } catch (error) {
      console.error("Error fetching programs:", error);
    }
  };

  const getFilteredApplications = async () => {
    setLoading(true);

    const skip = (page - 1) * limit;

    const payload = {
      campaign_ids: selectedCampaigns.map((item) => item.value),
      campus_ids: selectedCampuses.map((item) => item.value),
      department_ids: selectedDepartments.map((item) => item.value),
      program_ids: selectedPrograms.map((item) => item.value),
      quota_ids: selectedQuotas.map((item) => item.value),
      program_levels: selectedProgramLevels.map((item) => item.value),
      gender: selectedGender ? selectedGender.value : null,
      nationality: selectedNationality ? selectedNationality.value : null,
      is_paid: isPaid,
      is_added: isAdded,
      is_cancel: isCancel,
      search: debouncedSearch.trim() || null,
      skip: skip,
      limit: limit,
    };

    const requestOptions = {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(payload),
    };

    try {
      const response = await fetch(
        `${BASE_URL}/application/get_filtered_pre_register_applicants`,
        requestOptions
      );
      const data = await response.json();

      if (response.ok && data.selected_applicants) {
        setApplications(data.selected_applicants);
        setTotalCount(data.total_count || 0);
      } else {
        setApplications([]);
        setTotalCount(0);
        toast.info("No applications found matching your criteria");
      }
    } catch (error) {
      console.error("Error fetching applications:", error);
      toast.error("Error fetching applications");
      setApplications([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };

  const handleExportCSV = async () => {
  toast.info("Preparing export... Please wait.");

  // Fetch ALL applicants without pagination
  const payload = {
    campaign_ids: selectedCampaigns.map(item => item.value),
    campus_ids: selectedCampuses.map(item => item.value),
    department_ids: selectedDepartments.map(item => item.value),
    program_ids: selectedPrograms.map(item => item.value),
    quota_ids: selectedQuotas.map(item => item.value),
    program_levels: selectedProgramLevels.map(item => item.value),
    gender: selectedGender ? selectedGender.value : null,
    nationality: selectedNationality ? selectedNationality.value : null,
    is_paid: isPaid,
    is_added: isAdded,
    is_cancel: isCancel,
    search: debouncedSearch.trim() || null,
    skip: 0,
    limit: totalCount // Export all records
  };

  const requestOptions = {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(payload),
  };

  try {
    const response = await fetch(
      `${BASE_URL}/application/get_filtered_pre_register_applicants`,
      requestOptions
    );
    const data = await response.json();

    if (response.ok && data.selected_applicants) {
      const allApplicants = data.selected_applicants;

      const exportData = allApplicants.map((app, index) => ({
        serial: index + 1,
        name: app.name,
        cnic: app.cnic || "",
        gender: app.gender,
        nationality: app.nationality || "",
        father_name: app.father_name,
        city: app.city || "",
        address: app.current_address || "",
        phone: app.phone || "",
        department: app.department_name || "",
        program: app.program_name || "",
        program_level: app.program_level || "",
        quota: app.quota_name || "",
        voucher_status: app.voucher_status ? "Paid" : "Not Paid",
        is_added: app.is_added ? "Added" : "Not Added",
        is_cancel: app.is_cancel ? "Cancel" : "Not Cancel"
      }));

      const csv = Papa.unparse(exportData);
      const blob = new Blob([csv], { type: "text/csv" });
      const link = document.createElement("a");
      link.href = window.URL.createObjectURL(blob);
      link.download = `Selected_Applicants_${new Date().toISOString().split('T')[0]}.csv`;
      link.click();

      toast.success(`Successfully exported ${allApplicants.length} applicants!`);
    } else {
      toast.error("No data to export");
    }
  } catch (error) {
    console.error("Error exporting CSV:", error);
    toast.error("Error exporting CSV");
  }
};

  const handlePageChange = (event, value) => {
    setPage(value);
  };

  const handleViewApplication = (id) => {
    window.open(`/admissions/get-applications/${id}`);
  };

  // WhatsApp Dialog Handlers
  const handleOpenWhatsappDialog = () => {
    if (totalCount === 0) {
      toast.warning("No applicants found to send messages to");
      return;
    }
    setWhatsappDialogOpen(true);
  };

  const handleCloseWhatsappDialog = () => {
    setWhatsappDialogOpen(false);
    setWhatsappMessages([""]);
  };

  const handleAddMessage = () => {
    setWhatsappMessages([...whatsappMessages, ""]);
  };

  const handleRemoveMessage = (index) => {
    if (whatsappMessages.length > 1) {
      const newMessages = whatsappMessages.filter((_, i) => i !== index);
      setWhatsappMessages(newMessages);
    }
  };

  const handleMessageChange = (index, value) => {
    const newMessages = [...whatsappMessages];
    newMessages[index] = value;
    setWhatsappMessages(newMessages);
  };

  const handleSendWhatsapp = async () => {
    const validMessages = whatsappMessages.filter((msg) => msg.trim() !== "");
    if (validMessages.length === 0) {
      toast.error("Please enter at least one message");
      return;
    }

    setSendingWhatsapp(true);

    const payload = {
      campaign_ids: selectedCampaigns.map((item) => item.value),
      campus_ids: selectedCampuses.map((item) => item.value),
      department_ids: selectedDepartments.map((item) => item.value),
      program_ids: selectedPrograms.map((item) => item.value),
      quota_ids: selectedQuotas.map((item) => item.value),
      program_levels: selectedProgramLevels.map((item) => item.value),
      gender: selectedGender ? selectedGender.value : null,
      nationality: selectedNationality ? selectedNationality.value : null,
      is_paid: isPaid,
      is_added: isAdded,
      is_cancel: isCancel,
      messages: validMessages,
    };

    const requestOptions = {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(payload),
    };

    try {
      const response = await fetch(
        `${BASE_URL}/application/send_whatsapp_to_filtered_selected_applicant`,
        requestOptions
      );
      const data = await response.json();

      if (response.ok) {
        toast.success("WhatsApp messages sent successfully!");
        handleCloseWhatsappDialog();
      } else {
        toast.error(data.message || "Failed to send WhatsApp messages");
      }
    } catch (error) {
      console.error("Error sending WhatsApp messages:", error);
      toast.error("Error sending WhatsApp messages");
    }

    setSendingWhatsapp(false);
  };

  // Select component styling
  const customStyles = {
    control: (provided) => ({
      ...provided,
      minHeight: "42px",
      borderRadius: "8px",
    }),
    menu: (provided) => ({
      ...provided,
      zIndex: 9999,
    }),
  };

  // Status options
  const voucherStatusOptions = [
    { value: "all", label: "All" },
    { value: "paid", label: "Paid" },
    { value: "not_paid", label: "Not Paid" },
  ];

  const addedStatusOptions = [
    { value: "all", label: "All" },
    { value: "added", label: "Added" },
    { value: "not_added", label: "Not Added" },
  ];

  const cancelStatusOptions = [
    { value: "all", label: "All" },
    { value: "cancel", label: "Cancel" },
    { value: "not_cancel", label: "Not Cancel" },
  ];

  const handleVoucherStatusChange = (option) => {
    if (!option || option.value === "all") {
      setIsPaid(null);
    } else if (option.value === "paid") {
      setIsPaid(true);
    } else if (option.value === "not_paid") {
      setIsPaid(false);
    }
    setPage(1);
  };

  const handleAddedStatusChange = (option) => {
    if (!option || option.value === "all") {
      setIsAdded(null);
    } else if (option.value === "added") {
      setIsAdded(true);
    } else if (option.value === "not_added") {
      setIsAdded(false);
    }
    setPage(1);
  };

  const handleCancelStatusChange = (option) => {
    if (!option || option.value === "all") {
      setIsCancel(null);
    } else if (option.value === "cancel") {
      setIsCancel(true);
    } else if (option.value === "not_cancel") {
      setIsCancel(false);
    }
    setPage(1);
  };

  const totalPages = Math.ceil(totalCount / limit);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pb={3}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <MDTypography variant="h4" fontWeight="medium" mb={2}>
              Filter Selected Applicants
            </MDTypography>

            {/* First Row of Filters */}
            <Grid container spacing={2} mb={2}>
              <Grid item xs={12} md={4}>
                <MDTypography variant="caption" fontWeight="bold">
                  Campaign
                </MDTypography>
                <Select
                  isMulti
                  name="campaigns"
                  options={campaignOptions}
                  placeholder="Select Campaigns"
                  value={selectedCampaigns}
                  onChange={(val) => {
                    setSelectedCampaigns(val);
                    setPage(1);
                  }}
                  styles={customStyles}
                  isDisabled={loading}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <MDTypography variant="caption" fontWeight="bold">
                  Campus
                </MDTypography>
                <Select
                  isMulti
                  name="campuses"
                  options={campusOptions}
                  placeholder="Select Campuses"
                  value={selectedCampuses}
                  onChange={(val) => {
                    setSelectedCampuses(val);
                    setPage(1);
                  }}
                  styles={customStyles}
                  isDisabled={loading}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <MDTypography variant="caption" fontWeight="bold">
                  Department
                </MDTypography>
                <Select
                  isMulti
                  name="departments"
                  options={departmentOptions}
                  placeholder="Select Departments"
                  value={selectedDepartments}
                  onChange={(val) => {
                    setSelectedDepartments(val);
                    setPage(1);
                  }}
                  styles={customStyles}
                  isDisabled={!selectedCampuses.length || loading}
                />
              </Grid>
            </Grid>

            {/* Second Row of Filters */}
            <Grid container spacing={2} mb={2}>
              <Grid item xs={12} md={4}>
                <MDTypography variant="caption" fontWeight="bold">
                  Program
                </MDTypography>
                <Select
                  isMulti
                  name="programs"
                  options={programOptions}
                  placeholder="Select Programs"
                  value={selectedPrograms}
                  onChange={(val) => {
                    setSelectedPrograms(val);
                    setPage(1);
                  }}
                  styles={customStyles}
                  isDisabled={!selectedDepartments.length || loading}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <MDTypography variant="caption" fontWeight="bold">
                  Quota
                </MDTypography>
                <Select
                  isMulti
                  name="quotas"
                  options={quotaOptions}
                  placeholder="Select Quotas"
                  value={selectedQuotas}
                  onChange={(val) => {
                    setSelectedQuotas(val);
                    setPage(1);
                  }}
                  styles={customStyles}
                  isDisabled={loading}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <MDTypography variant="caption" fontWeight="bold">
                  Program Level
                </MDTypography>
                <Select
                  isMulti
                  name="programLevels"
                  options={programLevelOptions}
                  placeholder="Select Program Levels"
                  value={selectedProgramLevels}
                  onChange={(val) => {
                    setSelectedProgramLevels(val);
                    setPage(1);
                  }}
                  styles={customStyles}
                  isDisabled={loading}
                />
              </Grid>
            </Grid>

            {/* Third Row of Filters */}
            <Grid container spacing={2} mb={2}>
              <Grid item xs={12} md={3}>
                <MDTypography variant="caption" fontWeight="bold">
                  Gender
                </MDTypography>
                <Select
                  name="gender"
                  options={genderOptions}
                  placeholder="Select Gender"
                  value={selectedGender}
                  onChange={(val) => {
                    setSelectedGender(val);
                    setPage(1);
                  }}
                  isClearable
                  styles={customStyles}
                  isDisabled={loading}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <MDTypography variant="caption" fontWeight="bold">
                  Nationality
                </MDTypography>
                <Select
                  name="nationality"
                  options={nationalityOptions}
                  placeholder="Pakistani or Overseas"
                  value={selectedNationality}
                  onChange={(val) => {
                    setSelectedNationality(val);
                    setPage(1);
                  }}
                  isClearable
                  styles={customStyles}
                  isDisabled={loading}
                />
              </Grid>
              <Grid item xs={12} md={2}>
                <MDTypography variant="caption" fontWeight="bold">
                  Voucher Status
                </MDTypography>
                <Select
                  name="voucherStatus"
                  options={voucherStatusOptions}
                  placeholder="Select Voucher Status"
                  onChange={handleVoucherStatusChange}
                  isClearable
                  styles={customStyles}
                  isDisabled={loading}
                />
              </Grid>
              <Grid item xs={12} md={2}>
                <MDTypography variant="caption" fontWeight="bold">
                  Added Status
                </MDTypography>
                <Select
                  name="addedStatus"
                  options={addedStatusOptions}
                  placeholder="Select Added Status"
                  onChange={handleAddedStatusChange}
                  isClearable
                  styles={customStyles}
                  isDisabled={loading}
                />
              </Grid>
              <Grid item xs={12} md={2}>
                <MDTypography variant="caption" fontWeight="bold">
                  Cancel Status
                </MDTypography>
                <Select
                  name="cancelStatus"
                  options={cancelStatusOptions}
                  placeholder="Select Cancel Status"
                  onChange={handleCancelStatusChange}
                  isClearable
                  styles={customStyles}
                  isDisabled={loading}
                />
              </Grid>
            </Grid>
          </Grid>

          {/* Data Display Section */}
          <Grid item xs={12}>
            <Card>
              <MDBox mx={0} mt={0} py={2} px={2}>
                <Stack
                  direction="row"
                  spacing={2}
                  sx={{ width: "100%", mb: 2 }}
                >
                  <Paper
                    component="form"
                    sx={{
                      flex: 1,
                      p: "2px 4px",
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    <InputBase
                      sx={{ ml: 1, flex: 1, fontSize: "16px" }}
                      placeholder="Search by name, father name, or phone..."
                      value={searchKeyword}
                      onChange={(e) => setSearchKeyword(e.target.value)}
                      disabled={loading}
                    />
                    <SearchIcon sx={{ p: "10px", color: "action.active" }} />
                  </Paper>
                  {hasAdmissionPermission(
                    "Selected Applications",
                    "EXPORT CSV"
                  ) && (
                    <Button
                      variant="contained"
                      color="primary"
                      endIcon={<DescriptionIcon />}
                      onClick={handleExportCSV}
                      disabled={loading || applications.length === 0}
                      sx={{ color: "white" }}
                      style={{ color: "white" }}
                    >
                      Export CSV
                    </Button>
                  )}
                  <Button
                    variant="contained"
                    color="success"
                    endIcon={<WhatsAppIcon />}
                    onClick={handleOpenWhatsappDialog}
                    disabled={loading || totalCount === 0}
                    sx={{ color: "white" }}
                  >
                    Send WhatsApp
                  </Button>
                </Stack>

                {/* Total Count */}
                <Box
                  mb={2}
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <MDTypography variant="h6">
                    Total Applicants: {totalCount}
                  </MDTypography>
                  {loading && <CircularProgress size={24} />}
                </Box>

                {/* Bootstrap Table */}
                {loading ? (
                  <Box
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    minHeight="400px"
                  >
                    <CircularProgress />
                  </Box>
                ) : applications.length > 0 ? (
                  <>
                    <div className="table-responsive">
                      <table className="table table-hover" style ={{fontSize: '14px'}}>
                        <thead className="">
                          <tr>
                            <th scope="col" className="text-center">
                              Sr.No
                            </th>
                            <th scope="col" className="text-center">
                              Name
                            </th>
                            <th scope="col" className="text-center">
                              Gender
                            </th>
                            <th scope="col" className="text-center">
                              Nationality
                            </th>
                            <th scope="col" className="text-center">
                              Father Name
                            </th>
                            <th scope="col" className="text-center">
                              Department
                            </th>
                            <th scope="col" className="text-center">
                              Program
                            </th>
                            <th scope="col" className="text-center">
                              Program Level
                            </th>
                            <th scope="col" className="text-center">
                              Quota
                            </th>
                            <th scope="col" className="text-center">
                              Voucher Status
                            </th>
                            <th scope="col" className="text-center">
                              Is Added
                            </th>
                            <th scope="col" className="text-center">
                              Is Cancel
                            </th>
                            <th scope="col" className="text-center">
                              Actions
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {applications.map((app, index) => (
                            <tr key={app.selected_applicant_id || index}>
                              <td className="text-center">
                                {(page - 1) * limit + index + 1}
                              </td>
                              <td className="text-center">
                                {app.name || "N/A"}
                              </td>
                              <td className="text-center">
                                {app.gender || "N/A"}
                              </td>
                              <td className="text-center">
                                {app.nationality || "N/A"}
                              </td>
                              <td className="text-center">
                                {app.father_name || "N/A"}
                              </td>
                              <td className="text-center">
                                {app.department_name || "N/A"}
                              </td>
                              <td className="text-center">
                                {app.program_name || "N/A"}
                              </td>
                              <td className="text-center">
                                {app.program_level || "N/A"}
                              </td>
                              <td className="text-center">
                                {app.quota_name || "N/A"}
                              </td>
                              <td className="text-center">
                                <span
                                  className={`badge ${
                                    app.voucher_status
                                      ? "bg-success"
                                      : "bg-danger"
                                  }`}
                                >
                                  {app.voucher_status ? "Paid" : "Not Paid"}
                                </span>
                              </td>
                              <td className="text-center">
                                <span
                                  className={`badge ${
                                    app.is_added ? "bg-success" : "bg-warning"
                                  }`}
                                >
                                  {app.is_added ? "Added" : "Not Added"}
                                </span>
                              </td>
                              <td className="text-center">
                                <span
                                  className={`badge ${
                                    app.is_cancel ? "bg-danger" : "bg-success"
                                  }`}
                                >
                                  {app.is_cancel ? "Cancel" : "Not Cancel"}
                                </span>
                              </td>
                              <td className="text-center">
                                {hasAdmissionPermission(
                                  "Selected Applications",
                                  "READ"
                                ) && (
                                  <IconButton
                                    onClick={() =>
                                      handleViewApplication(
                                        app.id
                                      )
                                    }
                                    size="small"
                                  >
                                    <VisibilityIcon style={{ color: "blue" }} />
                                  </IconButton>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    {/* Pagination */}
                    {totalPages > 1 && (
                      <Box display="flex" justifyContent="center" mt={3}>
                        <Pagination
                          count={totalPages}
                          page={page}
                          onChange={handlePageChange}
                          color="primary"
                          disabled={loading}
                        />
                      </Box>
                    )}
                  </>
                ) : (
                  <Box textAlign="center" py={5}>
                    <img
                      src={nodata}
                      alt="No data"
                      style={{
                        width: "200px",
                        height: "200px",
                        margin: "auto",
                      }}
                    />
                    <MDTypography variant="h6" mt={2}>
                      No applicants found. Try adjusting your filters.
                    </MDTypography>
                  </Box>
                )}
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>

      {/* WhatsApp Dialog */}
      <Dialog
        open={whatsappDialogOpen}
        onClose={handleCloseWhatsappDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <WhatsAppIcon color="success" />
            Send WhatsApp Messages to Filtered Applicants
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary" mb={2}>
            Send messages to {totalCount} filtered applicants
          </Typography>

          {whatsappMessages.map((message, index) => (
            <Box key={index} mb={2}>
              <Box display="flex" alignItems="center" gap={1} mb={1}>
                <Typography variant="subtitle2">Message {index + 1}</Typography>
                {whatsappMessages.length > 1 && (
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => handleRemoveMessage(index)}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                )}
              </Box>
              <TextField
                fullWidth
                multiline
                rows={3}
                variant="outlined"
                placeholder="Enter your WhatsApp message..."
                value={message}
                onChange={(e) => handleMessageChange(index, e.target.value)}
              />
            </Box>
          ))}

          <Button
            startIcon={<AddIcon />}
            onClick={handleAddMessage}
            variant="outlined"
            color="primary"
            size="small"
            sx={{ mt: 1 }}
          >
            Add Another Message
          </Button>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCloseWhatsappDialog}
            disabled={sendingWhatsapp}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSendWhatsapp}
            variant="contained"
            color="success"
            disabled={sendingWhatsapp}
            startIcon={<WhatsAppIcon />}
            sx={{ color: "white" }}
          >
            {sendingWhatsapp ? "Sending..." : "Send Messages"}
          </Button>
        </DialogActions>
      </Dialog>

      <ToastContainer />
    </DashboardLayout>
  );
}

export default PreRegisterApplications;
